// Route Builder document + stats helpers (WEB-7046 follow-up).
//
// A saved route is a Measurement with measurementTypeId 2. Its geoJson is the same
// document the iOS (RouteDocument.swift) and Android (RouteGeoJson.kt) builders write,
// so a route made on any client opens and edits on the others:
//
//   FeatureCollection{features:[Feature{geometry:LineString [[lng,lat,elevM]...],
//     properties:{ line:{color,width,style}, routeWaypoints:[{latitude,longitude,name}],
//       routeMode:'drive'|'hike', routeFollows:{main,usfs,trails},
//       routeStats:{distanceMeters,elevationGainMeters,elevationLossMeters,
//                   maxElevationMeters,estimatedSeconds,layerMeters?,routedSeconds?},
//       routeSteps:[...] }}]}
//
// Dependency-free (no google.maps) so it is unit testable. All values are SI.

import { segmentDistanceMeters } from './trackStats'

export const ROUTE_MEASUREMENT_TYPE = 2
export const MAX_ROUTE_WAYPOINTS = 25

export const ROUTE_MODES = ['drive', 'hike']
export const DEFAULT_ROUTE_MODE = 'drive'
export const FOLLOW_KEYS = ['main', 'usfs', 'trails']
// Route Prefers defaults (saved as routeFollows) per travel mode (Matt, 2026-10-23; WEB-7138). Drive follows every
// drivable network (trails = motorized trails). Hike starts on forest roads and trails, off
// main roads, with Off-trail opt-in. A saved route keeps its own selection.
export const DEFAULT_FOLLOWS_BY_MODE = {
  drive: { main: true, usfs: true, trails: true, offTrail: false },
  hike: { main: false, usfs: true, trails: true, offTrail: false }
}
export const DEFAULT_FOLLOWS = DEFAULT_FOLLOWS_BY_MODE.drive
export const defaultFollows = mode => ({ ...(DEFAULT_FOLLOWS_BY_MODE[mode] || DEFAULT_FOLLOWS) })

// New-route style defaults, matching mobile (iOS RouteDocument.swift / Android TrackStyle.kt).
export const DEFAULT_ROUTE_COLOR = '#3399FF'
export const DEFAULT_ROUTE_LINE_STYLE = 'dots'
export const DEFAULT_ROUTE_WIDTH = 6

// Waypoint pin colors: start green, destination red, middle points white.
export const START_PIN_COLOR = '#9ECC3B'
export const END_PIN_COLOR = '#FF031C'
export const MID_PIN_COLOR = '#FFFFFF'

// Hiking pace (km/h) and climb cost (m of gain per hour) — Naismith-style, same on both apps.
const HIKE_KMH = 4.5
const CLIMB_M_PER_HOUR = 600
// Drive speeds by network layer (mph), used when the router gives no duration. A drive's
// trail stretches are motorized trails (API-3821); unlisted layers are walked.
const DRIVE_MPH = {
  trail: 10,
  core_road: 50,
  usgs_road: 30,
  local_road: 25,
  usfs_road: 15,
  connector: 20,
  roads: 30,
  mvum: 15
}
const DEFAULT_DRIVE_MPH = 30
const MPH_TO_MPS = 0.44704

/** Network rows the travel mode offers (both modes: under Drive, trails are motorized). */
export const followKeysForMode = () => FOLLOW_KEYS

/** Off-trail (cross-country over the terrain, API-3822) is a hiking option only. */
export const isOffTrail = (mode, follows) => mode === 'hike' && !!(follows && follows.offTrail)

/**
 * The Route/Get `layers` filter for a mode + follows selection, always explicit with a
 * mode (services read an omitted `layers` as every network). Returns [] for "nothing
 * selected", where the caller draws straight lines with no API call, ['none'] for an
 * off-trail path that follows no network, or the ticked layer names.
 */
export const routeLayers = (mode, follows = DEFAULT_FOLLOWS) => {
  const on = FOLLOW_KEYS.filter(k => follows[k] !== false)
  if (on.length) return on
  return isOffTrail(mode, follows) ? ['none'] : []
}

/** "All", "N of M", "Cross-country" or "Straight line" — the Route Prefers summary. */
export const followsSummary = (mode, follows = DEFAULT_FOLLOWS) => {
  const n = FOLLOW_KEYS.filter(k => follows[k] !== false).length
  if (n === 0) return isOffTrail(mode, follows) ? 'Cross-country' : 'Straight line'
  if (n === FOLLOW_KEYS.length) return 'All'
  return `${n} of ${FOLLOW_KEYS.length}`
}

/** Sum a Route/Get steps list's distance by network layer. */
export const layerMetersFromSteps = (steps) => {
  if (!Array.isArray(steps)) return null
  const out = {}
  steps.forEach(s => {
    if (!s || !s.layer || !(s.distanceMeters > 0)) return
    out[s.layer] = (out[s.layer] || 0) + s.distanceMeters
  })
  return Object.keys(out).length ? out : null
}

/**
 * Estimated travel time in seconds (iOS RouteStats.estimate / Android RouteTotals).
 * Hike: km / 4.5 + gain / 600 hours; off-trail uses the terrain router's walking time
 * (Tobler, slower off a trail) when it gives one.
 * Drive: the router's own duration when present; otherwise each layer's share of the
 * distance at that layer's speed, walking unlisted layers (trails) and charging their
 * share of the climb; with no layer data, 30 mph throughout.
 */
export const estimateRouteSeconds = ({ distanceMeters = 0, gainMeters = 0, mode = DEFAULT_ROUTE_MODE, layerMeters = null, routedSeconds = null, offTrail = false }) => {
  if (!(distanceMeters > 0)) return 0
  const hikeHours = (m, gain) => m / 1000 / HIKE_KMH + gain / CLIMB_M_PER_HOUR
  if (mode !== 'drive') {
    if (offTrail && routedSeconds > 0) return Math.round(routedSeconds)
    return Math.round(hikeHours(distanceMeters, gainMeters) * 3600)
  }
  if (routedSeconds > 0) return Math.round(routedSeconds)
  const layers = layerMeters && Object.keys(layerMeters).length ? layerMeters : null
  if (!layers) return Math.round(distanceMeters / (DEFAULT_DRIVE_MPH * MPH_TO_MPS))

  // Scale the step layer totals to the route's distance (steps and geometry differ slightly).
  const stepTotal = Object.values(layers).reduce((a, b) => a + b, 0)
  const k = stepTotal > 0 ? distanceMeters / stepTotal : 1
  let seconds = 0
  let footM = 0
  Object.keys(layers).forEach(layer => {
    const m = layers[layer] * k
    const mph = DRIVE_MPH[layer]
    if (mph) seconds += m / (mph * MPH_TO_MPS)
    else footM += m
  })
  if (footM > 0) seconds += hikeHours(footM, gainMeters * footM / distanceMeters) * 3600
  return Math.round(seconds)
}

/** "42m" / "1h 05m". */
export const formatRouteDuration = (seconds) => {
  const mins = Math.max(0, Math.round((seconds || 0) / 60))
  if (mins < 60) return `${mins}m`
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`
}

/** Slope bucket color for a grade in percent (absolute value). */
export const gradeColor = (g) => {
  const a = Math.abs(g || 0)
  if (a < 4) return '#86A94A'
  if (a < 10) return '#C9A23F'
  if (a < 18) return '#C67238'
  return '#B94038'
}

export const SLOPE_LEGEND = [
  { label: '<4', color: '#86A94A' },
  { label: '4–10', color: '#C9A23F' },
  { label: '10–18', color: '#C67238' },
  { label: '18+', color: '#B94038' }
]

/**
 * Per-vertex profile for coords [[lng,lat,elevM]...]:
 * [{ distM (cumulative), elevM, gradePct }]. Point 0 copies point 1's grade.
 */
export const buildRouteProfile = (coords = []) => {
  const out = []
  let cum = 0
  for (let i = 0; i < coords.length; i++) {
    let gradePct = 0
    if (i > 0) {
      const d = segmentDistanceMeters(coords[i - 1], coords[i])
      cum += d
      gradePct = d > 0 ? ((coords[i][2] - coords[i - 1][2]) / d) * 100 : 0
    }
    out.push({ distM: cum, elevM: coords[i][2] || 0, gradePct })
  }
  if (out.length > 1) out[0].gradePct = out[1].gradePct
  return out
}

/** Total distance (m) along coords. */
export const pathDistanceMeters = (coords = []) => {
  let d = 0
  for (let i = 1; i < coords.length; i++) d += segmentDistanceMeters(coords[i - 1], coords[i])
  return d
}

/** Raw gain / loss / max (m) over coords' elevations — no smoothing, as on mobile. */
export const elevationTotals = (coords = []) => {
  let gain = 0
  let loss = 0
  let max = coords.length ? -Infinity : 0
  for (let i = 0; i < coords.length; i++) {
    const e = coords[i][2] || 0
    if (e > max) max = e
    if (i > 0) {
      const d = e - (coords[i - 1][2] || 0)
      if (d > 0) gain += d
      else loss -= d
    }
  }
  return { gain, loss, max: Number.isFinite(max) ? max : 0 }
}

/**
 * Indices of at most n evenly spaced vertices (always including both ends) to send
 * to Elevation/Get.
 */
export const sampleIndices = (length, n) => {
  if (length <= n) return Array.from({ length }, (_, i) => i)
  const step = (length - 1) / (n - 1)
  const out = []
  for (let i = 0; i < n; i++) out.push(Math.round(i * step))
  return out
}

/**
 * Interpolate sampled elevations onto every vertex of coords by distance. A 0 means
 * "no coverage" and is filled from its neighbours (held at the ends). Returns new
 * coords [[lng,lat,elevM]...].
 */
export const applySampledElevations = (coords, indices, elevations) => {
  if (!coords.length || !indices.length) return coords.map(c => [c[0], c[1], 0])
  const vals = indices.map((_, i) => (elevations[i] > 0 || elevations[i] < 0 ? elevations[i] : null))
  // Fill no-coverage gaps from the nearest valid neighbour on each side.
  for (let i = 0; i < vals.length; i++) {
    if (vals[i] != null) continue
    let l = i - 1
    while (l >= 0 && vals[l] == null) l--
    let r = i + 1
    while (r < vals.length && vals[r] == null) r++
    vals[i] = l >= 0 && r < vals.length ? (vals[l] + vals[r]) / 2 : l >= 0 ? vals[l] : r < vals.length ? vals[r] : 0
  }
  const cum = [0]
  for (let i = 1; i < coords.length; i++) cum.push(cum[i - 1] + segmentDistanceMeters(coords[i - 1], coords[i]))
  const out = []
  let s = 0
  for (let i = 0; i < coords.length; i++) {
    while (s < indices.length - 2 && indices[s + 1] < i) s++
    const a = indices[s]
    const b = indices[Math.min(s + 1, indices.length - 1)]
    let e
    if (i <= a || a === b) e = vals[s]
    else if (i >= b) e = vals[Math.min(s + 1, vals.length - 1)]
    else {
      const span = cum[b] - cum[a]
      const t = span > 0 ? (cum[i] - cum[a]) / span : 0
      e = vals[s] + (vals[s + 1] - vals[s]) * t
    }
    out.push([coords[i][0], coords[i][1], Math.round(e * 10) / 10])
  }
  return out
}

/**
 * Straight legs between waypoints, densified to a vertex every ~50 m (at most 200 per
 * leg) so the elevation profile has shape. Used when Route Prefers is all off or the
 * router finds no path. Returns [[lng,lat]...].
 */
export const straightLinePath = (points, spacingM = 50, maxPerLeg = 200) => {
  const out = []
  for (let i = 0; i < points.length; i++) {
    const p = [points[i].lng, points[i].lat]
    if (i === 0) { out.push(p); continue }
    const a = [points[i - 1].lng, points[i - 1].lat]
    const n = Math.min(maxPerLeg, Math.max(1, Math.ceil(segmentDistanceMeters(a, p) / spacingM)))
    for (let k = 1; k <= n; k++) out.push([a[0] + (p[0] - a[0]) * k / n, a[1] + (p[1] - a[1]) * k / n])
  }
  return out
}

/**
 * True when every vertex carries a usable elevation ([lng,lat,elev], elev not 0), so the
 * separate Elevation/Get can be skipped. Off-trail routes return it; 0 means no coverage.
 */
export const hasFullElevation = (path = []) =>
  path.length > 1 && path.every(c => c.length > 2 && typeof c[2] === 'number' && Number.isFinite(c[2]) && c[2] !== 0)

/** Join a GeoJSON LineString / MultiLineString into one [[lng,lat]...] path. */
export const geometryToPath = (geometry) => {
  if (!geometry) return []
  if (geometry.type === 'LineString') return geometry.coordinates || []
  if (geometry.type === 'MultiLineString') {
    const out = []
    ;(geometry.coordinates || []).forEach(part => {
      part.forEach((c, i) => {
        const last = out[out.length - 1]
        if (i === 0 && last && last[0] === c[0] && last[1] === c[1]) return
        out.push(c)
      })
    })
    return out
  }
  return []
}

/**
 * Where a new point goes among the waypoints: the cheapest straight-line insertion,
 * including before the start or after the destination (iOS bestInsertionIndex), so a
 * click beyond either end extends the route from that end.
 */
export const bestInsertionIndex = (points, p) => {
  if (points.length < 2) return points.length
  const d = (a, b) => segmentDistanceMeters([a.lng, a.lat], [b.lng, b.lat])
  let best = points.length
  let bestCost = d(points[points.length - 1], p)
  const prependCost = d(p, points[0])
  if (prependCost < bestCost) { best = 0; bestCost = prependCost }
  for (let i = 1; i < points.length; i++) {
    const cost = d(points[i - 1], p) + d(p, points[i]) - d(points[i - 1], points[i])
    if (cost < bestCost) { best = i; bestCost = cost }
  }
  return best
}

/** Cumulative route distance (m) at each waypoint, by nearest path vertex in order. */
export const waypointDistances = (points, coords) => {
  if (!points.length) return []
  if (coords.length < 2) return points.map(() => 0)
  const cum = [0]
  for (let i = 1; i < coords.length; i++) cum.push(cum[i - 1] + segmentDistanceMeters(coords[i - 1], coords[i]))
  let from = 0
  return points.map((p, wi) => {
    if (wi === 0) return 0
    if (wi === points.length - 1) return cum[cum.length - 1]
    let bi = from
    let bd = Infinity
    for (let i = from; i < coords.length; i++) {
      const dd = segmentDistanceMeters(coords[i], [p.lng, p.lat])
      if (dd < bd) { bd = dd; bi = i }
    }
    from = bi
    return cum[bi]
  })
}

export const isDefaultRouteName = name => !name || /^Route - \d+$/.test(name)
export const defaultRouteName = existingCount => `Route - ${(existingCount || 0) + 1}`

/** Placeholder label for waypoint i of n (a lone pin is the destination). */
export const waypointLabel = (i, n) => {
  if (n === 1 || (i === n - 1 && n > 1)) return 'Destination'
  if (i === 0) return 'Start'
  return `Waypoint ${i + 1}`
}

export const waypointPinColor = (i, n) => {
  if (n === 1 || i === n - 1) return END_PIN_COLOR
  if (i === 0) return START_PIN_COLOR
  return MID_PIN_COLOR
}

/** Build the saved geoJson string. */
export const buildRouteGeoJson = ({ coords, waypoints, mode, follows, line, stats, steps }) => {
  const routeStats = {
    distanceMeters: Math.round((stats.distanceMeters || 0) * 10) / 10,
    elevationGainMeters: Math.round((stats.gainMeters || 0) * 10) / 10,
    elevationLossMeters: Math.round((stats.lossMeters || 0) * 10) / 10,
    maxElevationMeters: Math.round((stats.maxMeters || 0) * 10) / 10,
    estimatedSeconds: Math.round(stats.estimatedSeconds || 0)
  }
  if (stats.layerMeters) routeStats.layerMeters = stats.layerMeters
  if (stats.routedSeconds > 0) routeStats.routedSeconds = Math.round(stats.routedSeconds)
  const properties = {
    line: { color: line.color, width: line.width, style: line.style },
    routeWaypoints: waypoints.map(w => ({ latitude: w.lat, longitude: w.lng, name: w.name || '' })),
    routeMode: mode,
    routeFollows: { main: follows.main !== false, usfs: follows.usfs !== false, trails: follows.trails !== false, offTrail: isOffTrail(mode, follows) },
    routeStats
  }
  if (Array.isArray(steps) && steps.length) properties.routeSteps = steps
  return JSON.stringify({
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: coords.map(c => [c[0], c[1], c[2] || 0]) },
      properties
    }]
  })
}

/** Parse a saved route's geoJson (string or object). Returns null when unreadable. */
export const parseRouteGeoJson = (geoJson) => {
  let geo = geoJson
  try {
    if (typeof geo === 'string') geo = JSON.parse(geo.replace(/:\s*-?Infinity/g, ': null'))
  } catch (e) {
    return null
  }
  const feature = geo && geo.features && geo.features[0]
  if (!feature) return null
  const p = feature.properties || {}
  const line = p.line || {}
  const coords = geometryToPath(feature.geometry).map(c => [c[0], c[1], c[2] || 0])
  const waypoints = (p.routeWaypoints || []).map(w => ({ lat: w.latitude, lng: w.longitude, name: w.name || '' }))
  const mode = ROUTE_MODES.includes(p.routeMode) ? p.routeMode : DEFAULT_ROUTE_MODE
  const f = p.routeFollows || {}
  return {
    coords,
    waypoints,
    mode,
    // A route without offTrail (older clients, drives) isn't off-trail.
    follows: { main: f.main !== false, usfs: f.usfs !== false, trails: f.trails !== false, offTrail: mode === 'hike' && f.offTrail === true },
    line: {
      color: line.color || DEFAULT_ROUTE_COLOR,
      style: line.style || 'solid',
      width: Number(line.width) > 0 ? Number(line.width) : 4
    },
    stats: p.routeStats || {},
    steps: Array.isArray(p.routeSteps) ? p.routeSteps : null
  }
}
